"use client";

import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import React, { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { RegistrationStepForm } from "./RegistrationStepForm";
import { RegistrationSummaryModal } from "./RegistrationSummaryModal";

export interface ExternalGuestInfo {
  emails: string[];
  officeId: string;
  date: Date;
  time: Date;
}

export interface RegisteredGuest {
  email: string;
  code?: string;
}

interface PostReservationVisitorPromptProps {
  guestInfo: ExternalGuestInfo;
  onClose: () => void;
}

type Phase = "confirm" | "register" | "summary";

export const PostReservationVisitorPrompt: React.FC<
  PostReservationVisitorPromptProps
> = ({ guestInfo, onClose }) => {
  const [phase, setPhase] = useState<Phase>("confirm");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [registeredGuests, setRegisteredGuests] = useState<RegisteredGuest[]>(
    []
  );

  const totalGuests = guestInfo.emails.length;
  const currentEmail = guestInfo.emails[currentIndex];
  const isLastGuest = currentIndex >= totalGuests - 1;

  const handleConfirm = useCallback(() => {
    setPhase("register");
  }, []);

  const handleDecline = useCallback(() => {
    onClose();
  }, [onClose]);

  const finishOrAdvance = useCallback(
    (registered?: RegisteredGuest) => {
      setRegisteredGuests((prev) => {
        const updated = registered ? [...prev, registered] : prev;

        if (isLastGuest) {
          const hasCodes = updated.some((g) => g.code);
          if (hasCodes) {
            setPhase("summary");
          } else {
            onClose();
          }
        } else {
          setCurrentIndex((i) => i + 1);
        }

        return updated;
      });
    },
    [isLastGuest, onClose]
  );

  if (phase === "confirm") {
    return (
      <ConfirmModal
        isOpen
        title="Pre-Register Visitors?"
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        onConfirm={async () => {
          handleConfirm();
        }}
        onCancel={handleDecline}
        confirmLabel="Yes"
        cancelLabel="No"
        size="sm"
      >
        <div className="flex flex-col gap-3 py-2">
          <p className="text-muted-foreground text-sm">
            You added {totalGuests} external guest{totalGuests > 1 ? "s" : ""}{" "}
            to this meeting. Would you like to pre-register them in Visitor
            Manager?
          </p>
          <ul className="list-disc pl-5 text-muted-foreground text-sm">
            {guestInfo.emails.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        </div>
      </ConfirmModal>
    );
  }

  if (phase === "summary") {
    return (
      <RegistrationSummaryModal
        registeredGuests={registeredGuests}
        onClose={onClose}
      />
    );
  }

  return (
    <RegistrationStepForm
      email={currentEmail}
      officeId={guestInfo.officeId}
      date={guestInfo.date}
      time={guestInfo.time}
      stepLabel={`${currentIndex + 1} of ${totalGuests}`}
      isLastGuest={isLastGuest}
      onSuccess={(email, code) => {
        toast.success(`Visitor ${email} pre-registered successfully`);
        finishOrAdvance({ email, code });
      }}
      onSkip={() => finishOrAdvance()}
      onCancel={onClose}
    />
  );
};
